import React, { useEffect, useState } from 'react';
import { useIntl } from '../../util/reactIntl';
import css from './CarouselHint.module.css';

const LOCALSTORAGE_KEY = 'carouselHintDismissed_v1';

const CarouselHint = ({ targetSelector, isMobile }) => {
  const intl = useIntl();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMobile) return;

    // Allow a query param override for testing: ?showHint=1
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const forceShow = urlParams?.get('showHint') === '1';

    const dismissed = typeof window !== 'undefined' && window.localStorage?.getItem(LOCALSTORAGE_KEY);

    // Determine if this page load was a reload (hard refresh) so we can always show the hint
    let isReload = false;
    try {
      const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      const nav0 = navEntries && navEntries[0];
      if (nav0 && nav0.type === 'reload') {
        isReload = true;
      } else if (performance && performance.navigation && performance.navigation.type === 1) {
        // fallback for older browsers: 1 === TYPE_RELOAD
        isReload = true;
      }
    } catch (e) {
      // ignore
    }

    if (dismissed && !forceShow && !isReload) return;

    // Delay showing a bit so layout stabilizes
    const t = setTimeout(() => setVisible(true), 500);

    // Hide on first interaction with the target or anywhere (touch/pointer/wheel/scroll)
    const target = targetSelector ? document.querySelector(targetSelector) : null;
    let hidden = false;
    const hide = () => {
      if (hidden) return;
      hidden = true;
      setVisible(false);
      try { window.localStorage?.setItem(LOCALSTORAGE_KEY, '1'); } catch (e) {}
    };

    const attachTo = (el) => {
      if (!el) return;
      el.addEventListener('pointerdown', hide, { passive: true });
      el.addEventListener('touchstart', hide, { passive: true });
      el.addEventListener('wheel', hide, { passive: true });
      el.addEventListener('pointermove', hide, { passive: true });
      el.addEventListener('scroll', hide, { passive: true });
    };

    attachTo(target);
    // Global listeners so any touch or touchpad swipe hides the hint
    window.addEventListener('pointerdown', hide, { passive: true });
    window.addEventListener('touchstart', hide, { passive: true });
    window.addEventListener('wheel', hide, { passive: true });
    window.addEventListener('pointermove', hide, { passive: true });
    window.addEventListener('scroll', hide, { passive: true });

    return () => {
      clearTimeout(t);
      if (target) {
        target.removeEventListener('pointerdown', hide);
        target.removeEventListener('touchstart', hide);
        target.removeEventListener('wheel', hide);
        target.removeEventListener('pointermove', hide);
        target.removeEventListener('scroll', hide);
      }
      window.removeEventListener('pointerdown', hide);
      window.removeEventListener('touchstart', hide);
      window.removeEventListener('wheel', hide);
      window.removeEventListener('pointermove', hide);
      window.removeEventListener('scroll', hide);
    };
  }, [targetSelector, isMobile]);

  if (!visible) return null;

  return (
    <div className={`${css.hintRoot} ${css.hiddenOnDesktop}`} aria-hidden>
      <div className={css.hintCircle}>
        <svg className={css.arrowSvg} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M6 12h12" />
          <path fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M12 6l6 6-6 6" />
        </svg>
      </div>
    </div>
  );
};

export default CarouselHint;
